import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, RotateCcw, Upload, Database, History } from "lucide-react";
import { toast } from "sonner";

/**
 * Restore panel for Super Admin.
 *
 * Two modes:
 *   1. Full restore  — wipe & replace every tenant collection. Requires typing
 *                      the literal word RESTORE.
 *   2. Hospital-only — delete the target tenant's rows and reinsert from
 *                      snapshot. Requires typing the hospital code.
 *
 * System collections (users, plans, gateways, saas_settings, audit_logs,
 * backup_runs) are always preserved so the current admin can still log in.
 */
export default function RestorePanel({ hospitals = [] }) {
  const [snapshots, setSnapshots] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("hospital"); // "hospital" | "full"
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedHospital, setSelectedHospital] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [uploadLabel, setUploadLabel] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: sd }, { data: hd }] = await Promise.all([
        api.get("/superadmin/restore/list"),
        api.get("/superadmin/restore/history").catch(() => ({ data: { items: [] } })),
      ]);
      setSnapshots(sd.items || []);
      setHistory(hd.items || []);
      if ((sd.items || [])[0] && !selectedDate) setSelectedDate(sd.items[0].date);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load snapshots");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const targetHospital = useMemo(
    () => hospitals.find((h) => h.id === selectedHospital) || null,
    [hospitals, selectedHospital]
  );
  const expectedConfirm = mode === "full" ? "RESTORE" : (targetHospital?.code || "");
  const canRun =
    selectedDate &&
    (mode === "full" || selectedHospital) &&
    confirmText.trim().toUpperCase() === (expectedConfirm || "").toUpperCase() &&
    !!expectedConfirm;

  const uploadZip = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a .zip snapshot first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (uploadLabel.trim()) fd.append("label", uploadLabel.trim());
      const { data } = await api.post("/superadmin/restore/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: uploadLabel.trim() ? { label: uploadLabel.trim() } : {},
      });
      toast.success(`Snapshot ${data.date} added (${data.files} files)`);
      setUploadLabel("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
      setSelectedDate(data.date);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const runRestore = async () => {
    if (!canRun) return;
    const question =
      mode === "full"
        ? `Full restore from ${selectedDate}?\n\nThis wipes every tenant collection and reinserts them from the snapshot. Login credentials & subscription settings are preserved.\n\nA pre-restore snapshot will be taken automatically.\n\nContinue?`
        : `Restore hospital ${targetHospital?.name} (${targetHospital?.code}) from ${selectedDate}?\n\nThis deletes only this hospital's rows and reinserts them from the snapshot.\n\nContinue?`;
    if (!window.confirm(question)) return;

    setBusy(true);
    try {
      const url = mode === "full" ? "/superadmin/restore/full" : "/superadmin/restore/hospital";
      const body =
        mode === "full"
          ? { backup_date: selectedDate, confirm: confirmText.trim().toUpperCase() }
          : { backup_date: selectedDate, hospital_id: selectedHospital, confirm_code: confirmText.trim() };
      const { data } = await api.post(url, body);
      const total = Object.values(data.restored_counts || {}).reduce((s, n) => s + n, 0);
      toast.success(`Restore complete — ${total.toLocaleString("en-IN")} docs across ${Object.keys(data.restored_counts || {}).length} collections`);
      setConfirmText("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid="restore-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <RotateCcw size={16} className="text-brand" />
          <div>
            <div className="font-heading text-base font-semibold">Restore from backup</div>
            <div className="text-xs text-ink-muted">
              Choose a snapshot, then either restore a single client or the entire system.
              A <b>pre-restore snapshot</b> is captured automatically so the operation is reversible.
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
        {/* Left: run restore */}
        <div className="space-y-3 rounded-md border border-border bg-white p-3">
          <div>
            <Label>Backup snapshot</Label>
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
              data-testid="restore-snapshot-select"
            >
              <option value="">— select a snapshot —</option>
              {snapshots.map((s) => (
                <option key={s.date} value={s.date}>
                  {s.date} · {s.collections} collections · {(s.size_bytes / 1024).toFixed(1)} KB
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setMode("hospital"); setConfirmText(""); }}
              className={`rounded-md border px-2 py-1.5 text-xs ${mode === "hospital" ? "border-brand bg-brand text-white" : "border-border bg-white text-ink hover:bg-slate-50"}`}
              data-testid="restore-mode-hospital"
            >
              Client-wise
            </button>
            <button
              type="button"
              onClick={() => { setMode("full"); setConfirmText(""); }}
              className={`rounded-md border px-2 py-1.5 text-xs ${mode === "full" ? "border-rose-700 bg-rose-700 text-white" : "border-border bg-white text-ink hover:bg-slate-50"}`}
              data-testid="restore-mode-full"
            >
              Full restore (all tenants)
            </button>
          </div>

          {mode === "hospital" && (
            <div>
              <Label>Hospital (client)</Label>
              <select
                value={selectedHospital}
                onChange={(e) => { setSelectedHospital(e.target.value); setConfirmText(""); }}
                className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
                data-testid="restore-hospital-select"
              >
                <option value="">— select hospital —</option>
                {hospitals.map((h) => (
                  <option key={h.id} value={h.id}>{h.name} ({h.code})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label>
              Type <span className="font-mono text-rose-700">{expectedConfirm || "…"}</span> to confirm
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expectedConfirm ? `Type ${expectedConfirm}` : "Pick a hospital or mode first"}
              className="mt-1 font-mono text-sm"
              data-testid="restore-confirm-input"
              disabled={!expectedConfirm}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-ink-muted">
              Preserved: <span className="font-mono">users, plans, payment_gateways, saas_settings, audit_logs</span>.
            </div>
            <Button
              onClick={runRestore}
              disabled={!canRun || busy}
              className={mode === "full" ? "bg-rose-700 hover:bg-rose-800" : "bg-brand hover:bg-brand-700"}
              data-testid="restore-run-btn"
            >
              <AlertTriangle size={14} className="mr-2" />
              {busy ? "Restoring…" : mode === "full" ? "Run full restore" : "Restore hospital"}
            </Button>
          </div>
        </div>

        {/* Right: upload snapshot */}
        <div className="space-y-3 rounded-md border border-dashed border-brand/40 bg-brand/5 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-brand">
            <Upload size={14} />Upload snapshot ZIP
          </div>
          <div className="text-[11px] text-ink-muted">
            Bring in a backup from another server. Accepts the same
            <span className="mx-1 font-mono">.jsonl.gz</span> layout produced by the daily backup.
          </div>
          <div>
            <Label>Label (optional)</Label>
            <Input
              value={uploadLabel}
              onChange={(e) => setUploadLabel(e.target.value)}
              placeholder="e.g. 2026-09-01-manual"
              className="mt-1 text-sm"
              data-testid="restore-upload-label"
            />
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              className="block w-full text-xs"
              data-testid="restore-upload-file"
            />
          </div>
          <Button onClick={uploadZip} disabled={busy} className="bg-brand-700 hover:bg-brand-800" data-testid="restore-upload-btn">
            <Upload size={14} className="mr-2" />Upload snapshot
          </Button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="border-t border-border">
          <div className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-ink-muted">
            <History size={12} />Recent restores
          </div>
          <table className="w-full text-xs">
            <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-1.5 text-left">When</th>
                <th className="px-3 py-1.5 text-left">Kind</th>
                <th className="px-3 py-1.5 text-left">Snapshot</th>
                <th className="px-3 py-1.5 text-left">Hospital</th>
                <th className="px-3 py-1.5 text-right">Docs restored</th>
                <th className="px-3 py-1.5 text-left">Actor</th>
                <th className="px-3 py-1.5 text-right">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {history.map((h, i) => {
                const total = Object.values(h.restored_counts || {}).reduce((s, n) => s + n, 0);
                const hospName = hospitals.find((x) => x.id === h.hospital_id)?.name || h.hospital_id || "—";
                return (
                  <tr key={i}>
                    <td className="px-3 py-1.5 font-mono text-[10px]">{h.started_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-1.5">
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${h.kind === "full" ? "bg-rose-100 text-rose-700" : "bg-brand/10 text-brand"}`}>{h.kind}</span>
                    </td>
                    <td className="px-3 py-1.5 font-mono">{h.backup_date}</td>
                    <td className="px-3 py-1.5">{h.kind === "full" ? "—" : hospName}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{total.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-1.5 text-[10px] text-ink-muted">{h.actor}</td>
                    <td className="px-3 py-1.5 text-right text-[10px]">{h.duration_sec?.toFixed(1) || 0}s</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
